import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

interface TestimonialItem {
  name: string;
  role: string;
  quote: string;
}

// Variantes visuales de bubble (lavanda / mint / amarillo). Cada item del
// array agrupa el asset de comillas, el avatar redondo, el icono 3D inferior
// derecho y un modifier de color para la card. El orden coincide con el de
// los testimonios en i18n para que items[i] reciba styles[i].
const STYLES = [
  {
    variant: "lavender",
    bubble: "/images/clients/BOCADILLO_1.webp",
    quoteIcon: "/images/clients/ICONO_COMILLAS_LILA.webp",
    avatar: "/images/clients/1-CLIENTE.webp",
    icon3d: "/images/clients/ICONO_1.webp",
  },
  {
    variant: "mint",
    bubble: "/images/clients/BOCADILLO_2.webp",
    quoteIcon: "/images/clients/ICONO_COMILLAS_MINT.webp",
    avatar: "/images/clients/2-CLIENTE.webp",
    icon3d: "/images/clients/ICONO_2.webp",
  },
  {
    variant: "yellow",
    bubble: "/images/clients/BOCADILLO3.webp",
    quoteIcon: "/images/clients/ICONO_COMILLAS_AMARILLO.webp",
    avatar: "/images/clients/3-CLIENTE.webp",
    icon3d: "/images/clients/ICONO_3.webp",
  },
] as const;

// IntersectionObserver one-shot — añade .is-visible para reproducir la
// entrada por scroll. Mismo patrón que ForChainsTeaser.
function useReveal() {
  const ref = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          obs.disconnect();
        }
      },
      { threshold: 0.15 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return { ref, visible };
}

export default function Testimonials() {
  const { t } = useTranslation();
  const items =
    (t("testimonials.items", { returnObjects: true }) as unknown as TestimonialItem[]) || [];
  const { ref, visible } = useReveal();

  return (
    <section
      id="testimonials"
      ref={ref}
      className={`playium-testimonials${visible ? " is-visible" : ""}`}
    >
      <div className="playium-testimonials__inner">
        <div className="playium-testimonials__header">
          <img loading="lazy"
            src="/images/clients/ICONO_CLIENTES.webp"
            alt=""
            aria-hidden="true"
            className="playium-testimonials__hero-icon"
          />
          <h2 className="playium-testimonials__title playium-heading">
            {t("testimonials.title")}
          </h2>
        </div>

        <div className="playium-testimonials__grid">
          {items.map((tm, i) => {
            const style = STYLES[i] ?? STYLES[0];
            return (
              <article
                key={i}
                className={`playium-testimonial playium-testimonial--${style.variant}`}
                style={{ ["--reveal-delay" as never]: `${i * 120}ms` }}
              >
                <img loading="lazy"
                  src={style.bubble}
                  alt=""
                  aria-hidden="true"
                  className="playium-testimonial__bubble"
                />
                <div className="playium-testimonial__top">
                  <img loading="lazy"
                    src={style.quoteIcon}
                    alt=""
                    aria-hidden="true"
                    className="playium-testimonial__quote-icon"
                  />
                  <img loading="lazy"
                    src="/images/clients/ESTRELLAS.webp"
                    alt=""
                    aria-hidden="true"
                    className="playium-testimonial__stars"
                  />
                </div>

                <p className="playium-testimonial__quote">"{tm.quote}"</p>

                <div className="playium-testimonial__footer">
                  <img loading="lazy"
                    src={style.avatar}
                    alt=""
                    aria-hidden="true"
                    className="playium-testimonial__avatar"
                  />
                  <div className="playium-testimonial__person">
                    <p className="playium-testimonial__name">{tm.name}</p>
                    <p className="playium-testimonial__role">{tm.role}</p>
                  </div>
                  <img loading="lazy"
                    src={style.icon3d}
                    alt=""
                    aria-hidden="true"
                    className="playium-testimonial__icon3d"
                  />
                </div>
              </article>
            );
          })}
        </div>

        <p className="playium-testimonials__disclaimer">
          {t("testimonials.disclaimer")}
        </p>
      </div>
    </section>
  );
}
